import { useMemo, useState } from 'react'
import { HeaderContext } from '@tanstack/react-table'
import clsx from 'clsx'

import { Button } from '@/ui/Button'
import { Popup } from '@/ui/Popup'
import { Checkbox } from '@/ui/Checkbox'
import { Input } from '@/ui/Input'

import styles from './CommonHeader.module.scss'
import commonStyles from './Common.module.scss'

import ArrowDownIcon from '../../assets/arrow-down.svg'

export type FilterOption = {
    label: string
    value: string
}

const isFilterOption = (value: string | number | FilterOption): value is FilterOption => {
    return typeof value === 'object' && value !== null && 'label' in value && 'value' in value
}

type Props<T> = HeaderContext<T, unknown> & {
    label: string
    searchPlaceholder?: string
    buttonLabel?: string
}

export const CommonHeader = <T,>({ table, column, label, buttonLabel, searchPlaceholder }: Props<T>) => {
    const [selectedValues, setSelectedValues] = useState<string[]>((column.getFilterValue() as string[]) || [])
    const [search, setSearch] = useState('')
    const state = table.getState()
    const parseColumnValue = column.columnDef.meta?.parseColumnValue
    const formatFilterValues = column.columnDef.meta?.formatFilterValues

    const isActive = useMemo(
        () =>
            !!state.sorting.find((item) => item.id === column.id) ||
            !!state.columnFilters.find((item) => item.id === column.id),
        [state]
    )

    const columnValues = table
        .getCoreRowModel()
        .rows.reduce<(string | string[])[]>((acc, row) => {
            const value = row.getValue(column.id)

            if (!value) {
                return acc
            }
            const parsedValue = parseColumnValue ? parseColumnValue(value) : (value as string | string[])
            return Array.isArray(parsedValue) ? [...acc, ...parsedValue] : [...acc, parsedValue]
        }, [])
        .flat()

    const uniqColumnValues = Array.from(new Set(columnValues))
    const formattedValues = formatFilterValues ? formatFilterValues(uniqColumnValues, table) : uniqColumnValues

    const options = useMemo(
        () =>
            formattedValues.reduce<FilterOption[]>((acc, value) => {
                if (isFilterOption(value)) {
                    if (
                        value.label.toLowerCase().includes(search.toLowerCase()) ||
                        value.value.toLowerCase().includes(search.toLowerCase())
                    ) {
                        acc.push(value)
                    }
                } else if (value.toString().toLowerCase().includes(search.toLowerCase())) {
                    acc.push({
                        label: value.toString(),
                        value: value.toString().toLocaleLowerCase(),
                    })
                }

                return acc
            }, []),
        [search, formattedValues]
    )

    const handleSelectValue = (value: string) => {
        let updatedValues = [...selectedValues]

        if (checkIsRowSelected(value)) {
            updatedValues = updatedValues.filter((item) => item !== value)
        } else {
            updatedValues.push(value)
        }

        setSelectedValues(updatedValues)
        column.setFilterValue(updatedValues.length ? updatedValues : undefined)
    }

    const handleSortColumn = () => {
        column.toggleSorting(false)
    }

    const checkIsRowSelected = (value: string) => selectedValues.includes(value)
    const clearValues = () => {
        setSelectedValues([])
        column.setFilterValue(undefined)
    }

    return (
        <Popup
            triggerComponent={(open: boolean) => (
                <Button
                    size="small"
                    variant="transparent"
                    disabled={!options.length && !open}
                    className={clsx(commonStyles.trigger, { [commonStyles.active]: open || isActive })}
                >
                    {label}
                    <ArrowDownIcon />
                </Button>
            )}
            attachToTrigger
            placement="bottom-start"
            className={commonStyles.popup}
        >
            <div className={styles.container}>
                <div className={styles.searchInputWrapper}>
                    <Input
                        value={search}
                        fullWidth
                        allowClear
                        placeholder={searchPlaceholder}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
                <div className={styles.scrollContainer}>
                    {buttonLabel && (
                        <div className={styles.actionWrapper}>
                            <Button
                                variant="transparent"
                                size="small"
                                className={styles.action}
                                onClick={handleSortColumn}
                            >
                                {buttonLabel}
                            </Button>
                        </div>
                    )}
                    <div className={styles.optionsWrapper}>
                        <div className={styles.optionsActions}>
                            <p className={styles.selectedLabel}>{selectedValues.length} selected</p>
                            <button
                                className={clsx(styles.clear, {
                                    [styles.clearActive]: !!selectedValues.length,
                                })}
                                onClick={clearValues}
                            >
                                Clear
                            </button>
                        </div>
                        {options.map(({ label, value }, index) => (
                            <Button
                                key={index}
                                size="small"
                                variant="transparent"
                                onClick={() => handleSelectValue(value)}
                                className={styles.item}
                            >
                                <span>{label}</span>
                                <Checkbox variant="withoutLabel" checked={checkIsRowSelected(value)} />
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
        </Popup>
    )
}
